import {hpcAttrs} from '@github-ui/hpc'
import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {testIdProps} from '@github-ui/test-id-props'
import {useMemo, useRef} from 'react'

import {VALUES} from './constants/values'
import {MilestoneEmptyState} from './MilestoneEmptyState'
import {MilestoneListPagination} from './MilestoneListPagination'
import {MilestoneRow} from './MilestoneRow'
import {MilestoneSortMenu} from './MilestoneSortMenu'
import {OpenClosedMilestones} from './OpenClosedMilestones'
import type {RepositoryResult} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'

export type MilestoneListFragment = Pick<
  RepositoryResult,
  'nameWithOwner' | 'viewerCanCreateMilestones' | 'milestones' | 'open' | 'closed'
>

type MilestoneListProps = {
  repository: MilestoneListFragment
  queryKey?: RouteQueryKey
  onMutationSuccess?: (milestoneNumber: number) => void | Promise<void>
  onDeleteSuccess?: (milestoneNumber: number) => void | Promise<void>
}

export function MilestoneList({repository, queryKey, onMutationSuccess, onDeleteSuccess}: MilestoneListProps) {
  if (!repository.milestones) {
    throw new Error('Milestones data is null or undefined')
  }

  // Focus lands here when a mutation unmounts the acted-on row, so focus never falls to <body> (WCAG 2.4.3).
  const listFocusRef = useRef<HTMLDivElement>(null)

  const nodes = useMemo(() => {
    return (
      repository.milestones?.edges
        ?.map(edge => edge?.node)
        .filter((node): node is NonNullable<typeof node> => node != null) ?? []
    )
  }, [repository])

  const actions = useMemo(() => {
    return [
      {
        key: 'milestone-sort-menu',
        render: () => <MilestoneSortMenu />,
      },
    ]
  }, [])

  const rootUrl = `${ssrSafeLocation.origin}/${repository.nameWithOwner}`
  const newMilestoneUrl = `${rootUrl}/milestones/new`

  const listItemsHeader = (
    <ListViewMetadata
      actions={actions}
      actionsLabel="Actions"
      sectionFilters={<OpenClosedMilestones repository={repository} />}
    />
  )

  const renderMilestoneList = () => {
    if (nodes.length > 0) {
      return nodes.map(node => (
        <MilestoneRow
          key={node.id}
          milestone={node}
          repositoryNameWithOwner={repository.nameWithOwner}
          queryKey={queryKey}
          listFocusRef={listFocusRef}
          onMutationSuccess={onMutationSuccess}
          onDeleteSuccess={onDeleteSuccess}
        />
      ))
    }

    const openCount = repository.open?.totalCount ?? 0
    const closedCount = repository.closed?.totalCount ?? 0
    const noCreatedMilestones = openCount === 0 && closedCount === 0

    return (
      <MilestoneEmptyState
        noCreatedMilestones={noCreatedMilestones}
        newMilestoneUrl={newMilestoneUrl}
        viewerCanCreateMilestones={repository.viewerCanCreateMilestones}
      />
    )
  }

  return (
    <>
      <div
        ref={listFocusRef}
        tabIndex={-1}
        role="group"
        aria-label="Milestones"
        className={styles.milestoneListWrapper}
        {...hpcAttrs()}
      >
        <ListView
          {...testIdProps('repository-milestone-list-view')}
          title=""
          metadata={listItemsHeader}
          titleHeaderTag="h2"
          singularUnits={'milestone'}
          pluralUnits={'milestones'}
          as={nodes.length > 0 ? 'ul' : 'div'}
          role={nodes.length > 0 ? 'list' : 'status'}
        >
          {renderMilestoneList()}
        </ListView>
      </div>
      {(repository.milestones?.totalCount ?? 0) > VALUES.milestonePageSize && (
        <MilestoneListPagination dataCount={repository.milestones?.totalCount ?? 0} />
      )}
    </>
  )
}
